import styled from 'styled-components'
import { Input as FieldInput } from '@components/Field'
import { gold, black, textMuted, mutedGrey, white, grey, border1 } from '@/theme/colors'
import { shadows, radii, motion, layers, iconSizes } from '@/theme/tokens'
import { fontSizes, leading, text } from '@/theme/typography'
import { flatButton } from '@/theme/recipes'

export const AutocompleteContainer = styled.div`
	position: relative;
	width: 100%;
`

export const Input = styled(FieldInput)`
	padding-right: 40px;
`

export const ClearButton = styled.button`
	${flatButton}
	position: absolute;
	right: 12px;
	top: 50%;
	transform: translateY(-50%);
	width: 24px;
	height: 24px;
	border-radius: 50%;
	display: flex;
	align-items: center;
	justify-content: center;
	color: ${mutedGrey};
	font-size: ${fontSizes.headingLg};
	line-height: ${leading.none};
	transition:
		background-color ${motion.fast},
		color ${motion.fast};

	&:hover {
		background-color: ${grey};
		color: ${black};
	}
`

/** Portal-positioned popover list on the overlay shadow. */
export const AutocompleteDropdown = styled.div<{ $top: number; $left: number; $width: number }>`
	position: fixed;
	top: ${({ $top }) => $top}px;
	left: ${({ $left }) => $left}px;
	width: ${({ $width }) => $width}px;
	background: ${white};
	border: 1px solid ${border1};
	border-radius: ${radii.inner};
	box-shadow: ${shadows.overlay};
	max-height: 300px;
	overflow-y: auto;
	z-index: ${layers.popover};
`

export const AutocompleteItem = styled.div`
	padding: 12px 16px;
	cursor: pointer;
	transition: background-color ${motion.fast};

	&:hover {
		background-color: ${gold}1f;
	}

	&:not(:last-child) {
		border-bottom: 1px solid ${border1};
	}
`

export const EmptyAutocompleteItem = styled(AutocompleteItem)`
	cursor: default;
	color: ${mutedGrey};
	font-size: ${fontSizes.small};
`

/**
 * The trailing "use it as typed" row. Sits below the real matches and reads as
 * the quieter fallback it is, on the same density as its siblings.
 */
export const CustomLocationItem = styled(AutocompleteItem)`
	display: flex;
	align-items: center;
	gap: 12px;
	font-size: ${fontSizes.small};
	color: ${textMuted};
`

/** A place's row, as iOS lays it out: its type's icon, then its name over its address. */
export const PlaceItem = styled(AutocompleteItem)`
	display: flex;
	align-items: center;
	gap: 12px;
`

/** The column every row's icon sits in, so names line up whatever the glyph. */
export const IconSlot = styled.span<{ $muted?: boolean }>`
	flex: none;
	display: flex;
	align-items: center;
	justify-content: center;
	width: ${iconSizes.xl}px;
	color: ${({ $muted }) => ($muted ? mutedGrey : black)};
`

export const RowText = styled.div`
	min-width: 0;
`

export const MainText = styled.div`
	font-size: ${fontSizes.small};
	font-weight: 500;
	color: ${black};
	margin-bottom: 2px;
`

export const SecondaryText = styled.div`
	font-size: ${fontSizes.micro};
	color: ${textMuted};
`

/** A section's title in what the search opens on: "In this trip", "Popular in Tokyo". */
export const SectionHeader = styled.div`
	${text.labelSm}
	padding: 12px 16px 4px;
	color: ${textMuted};
	cursor: default;
`

/** "Show all 8": opens up the rest of the trip's places, so it reads quieter than a place. */
export const ShowAllItem = styled(AutocompleteItem)`
	display: flex;
	align-items: center;
	gap: 12px;
	font-size: ${fontSizes.small};
	color: ${mutedGrey};
`
